/**
 * Radial slider - cards rotate around a shared point far below the list,
 * so neighbouring cards fan out along an arc. Based on Osmo's Radial Cards Slider.
 *
 * Layout is controlled with CSS custom properties on the element:
 * --radial-rotate  Angle between the active card and its neighbours (e.g. 15deg)
 * --radial-radius  Transform origin Y of each card, as a % of its height (e.g. 350%)
 * --radial-scale   Scale of each card relative to the one before it, moving out from the centre (e.g. 0.65)
 *
 * Image cards with [data-zoom-src] open full screen in the [data-radial-zoom] dialog when the active card is clicked.
 */
class RadialSlider extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-radial-list]');
    this.originals = Array.from(this.list.querySelectorAll(':scope > [data-radial-item]'));
    this.dots = Array.from(this.querySelectorAll('[data-radial-dot]'));
    this.dotsWrap = this.querySelector('[data-radial-dots]');
    this.total = this.originals.length;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.duration = this.reducedMotion ? 0 : 0.8;
    this.lastDragEnd = 0;

    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick, true);
    this.setupZoom();
    this.trackLoading(this.originals.map(item => item.querySelector('[data-radial-card]')));
    if(this.total < 2) {
      return;
    }

    this.state = { position: parseInt(this.dataset.radialStart ?? 0) || 0 };
    this.activeSlide = null;

    this.render = this.render.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.addEventListener('keydown', this.onKeydown);
    this.dots.forEach(dot => {
      dot.addEventListener('click', () => this.goToSlide(parseInt(dot.dataset.radialDot)));
    });

    this.setup();
    this.createDraggable();

    let lastWidth = window.innerWidth;
    this.onResize = debounce(() => {
      if(window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      this.handleResize();
    }, 200);
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    this.draggable && this.draggable.kill();
    this.state && gsap.killTweensOf(this.state);
    this.onResize && window.removeEventListener('resize', this.onResize);
    this.zoom?.open && this.zoom.close();
    document.documentElement.style.overflow = '';
  }

  /**
   * Shows a spinner on each card until its image has loaded.
   */
  trackLoading(cards) {
    cards.forEach(card => {
      if(!card) return;
      const image = card.querySelector('img');
      const loaded = () => card.setAttribute('data-state', 'loaded');
      if(!image || (image.complete && image.naturalWidth > 0)) {
        loaded();
        return;
      }
      card.setAttribute('data-state', 'loading');
      image.addEventListener('load', loaded, { once: true });
      image.addEventListener('error', loaded, { once: true });
    });
  }

  /**
   * Measures the layout, adds enough clones for a seamless loop and renders.
   */
  setup() {
    this.list.querySelectorAll('[data-radial-clone]').forEach(clone => clone.remove());
    gsap.set(this.originals, { clearProps: 'rotation,zIndex,opacity,visibility' });
    this.style.paddingBottom = '';

    const styles = getComputedStyle(this);
    this.step = Math.abs(parseFloat(styles.getPropertyValue('--radial-rotate'))) || 15;
    this.scaleRatio = gsap.utils.clamp(0.2, 1, parseFloat(styles.getPropertyValue('--radial-scale')) || 1);

    // Distance along the arc to each card, so smaller cards sit closer together
    this.arcStops = [0];
    for(let k = 1; k <= 360; k++) {
      this.arcStops[k] = this.arcStops[k - 1] + (this.getScale(k - 1) + this.getScale(k)) / 2;
    }

    const first = this.originals[0];
    const itemWidth = first.offsetWidth;
    const itemHeight = first.offsetHeight;
    const originY = parseFloat(getComputedStyle(first).transformOrigin.split(' ')[1]) || itemHeight * 3.5;
    this.wheelRadius = Math.max(0, originY - itemHeight / 2);
    this.pxPerStep = Math.max(1, this.wheelRadius * this.step * Math.PI / 180);

    // How many cards fit on each side of the active card before going off screen
    // and how far below the list the lowest of them reaches
    const halfContainer = this.offsetWidth / 2;
    let visibleSide = 0;
    let lowestBottom = itemHeight;
    for(let k = 1; k < this.arcStops.length && this.getAngle(k) < 90; k++) {
      const rad = this.getAngle(k) * Math.PI / 180;
      const x = Math.sin(rad) * this.wheelRadius;
      const scale = this.getScale(k);
      if(x - itemWidth * scale / 2 > halfContainer) break;
      visibleSide = k;
      if(x <= halfContainer) {
        const centerY = originY - Math.cos(rad) * this.wheelRadius;
        const halfHeight = scale * (itemWidth * Math.sin(rad) + itemHeight * Math.cos(rad)) / 2;
        lowestBottom = Math.max(lowestBottom, centerY + halfHeight);
      }
    }
    this.visibleSide = visibleSide;

    // Push the content below the slider clear of the side cards
    const dotsHeight = this.dotsWrap ? this.dotsWrap.offsetHeight : 0;
    const overhang = lowestBottom - this.list.offsetHeight - dotsHeight;
    if(overhang > 0) {
      this.style.paddingBottom = `${Math.ceil(overhang)}px`;
    }

    // Cards wrap around at half the item count, so keep that point hidden beyond the visible cards
    const needed = 2 * visibleSide + 2;
    const copies = Math.max(1, Math.ceil(needed / this.total));
    for(let c = 1; c < copies; c++) {
      this.originals.forEach(item => {
        const clone = item.cloneNode(true);
        clone.setAttribute('data-radial-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-media-id');
        clone.removeAttribute('data-carousel-jump');
        clone.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
        clone.querySelectorAll('a, button, [tabindex]').forEach(el => el.setAttribute('tabindex', '-1'));
        this.list.appendChild(clone);
      });
    }

    this.items = Array.from(this.list.querySelectorAll(':scope > [data-radial-item]'));
    this.cards = this.items.map(item => item.querySelector('[data-radial-card]'));
    this.trackLoading(this.cards);
    this.setRotation = this.items.map(item => gsap.quickSetter(item, 'rotation', 'deg'));
    // quickSetter doesn't support the "scale" shorthand, so set both axes
    this.setScale = this.cards.map(card => {
      const setScaleX = gsap.quickSetter(card, 'scaleX');
      const setScaleY = gsap.quickSetter(card, 'scaleY');
      return scale => {
        setScaleX(scale);
        setScaleY(scale);
      };
    });
    this.activeSlide = null;
    this.render();
  }

  handleResize() {
    gsap.killTweensOf(this.state);
    const slide = this.getActiveSlide();
    this.state.position = slide;
    this.setup();
    this.syncProxy();
  }

  createDraggable() {
    // Invisible element Draggable moves along x; its offset is converted to a slide position
    this.proxy = document.createElement('div');
    this.proxy.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;visibility:hidden;pointer-events:none;';
    this.appendChild(this.proxy);

    const slider = this;
    let samples = [];
    let dragged = false;
    this.draggable = Draggable.create(this.proxy, {
      type: 'x',
      trigger: this.list,
      dragClickables: true,
      minimumMovement: 4,
      onPress() {
        gsap.killTweensOf(slider.state);
        slider.syncProxy();
        this.update();
        samples = [];
        dragged = false;
        slider.setAttribute('data-drag', 'grabbing');
      },
      onDragStart() {
        dragged = true;
      },
      onDrag() {
        const now = performance.now();
        samples.push({ x: this.x, t: now });
        samples = samples.filter(sample => now - sample.t < 100);
        slider.state.position = -this.x / slider.pxPerStep;
        slider.render();
      },
      onDragEnd() {
        slider.lastDragEnd = performance.now();
        const firstSample = samples[0];
        const lastSample = samples[samples.length - 1];
        const elapsed = firstSample && lastSample ? (lastSample.t - firstSample.t) / 1000 : 0;
        const velocity = elapsed > 0 ? (lastSample.x - firstSample.x) / elapsed : 0;
        // Project the throw forward a little, then snap to the nearest card
        const projected = -(this.x + velocity * 0.2) / slider.pxPerStep;
        slider.tweenTo(Math.round(projected));
      },
      onRelease() {
        slider.removeAttribute('data-drag');
        // A press without a drag may have stopped a tween part way, so settle on the nearest card
        if(!dragged) {
          slider.tweenTo(Math.round(slider.state.position));
        }
      }
    })[0];
    this.syncProxy();
  }

  syncProxy() {
    if(!this.proxy) return;
    gsap.set(this.proxy, { x: -this.state.position * this.pxPerStep });
  }

  getScale(offset) {
    return Math.max(0.2, Math.pow(this.scaleRatio, Math.abs(offset)));
  }

  /**
   * Rotation for a (possibly fractional) offset from the centre, in degrees.
   */
  getAngle(offset) {
    const distance = Math.min(Math.abs(offset), this.arcStops.length - 1);
    const floor = Math.floor(distance);
    const next = this.arcStops[Math.min(floor + 1, this.arcStops.length - 1)];
    const arc = this.arcStops[floor] + (next - this.arcStops[floor]) * (distance - floor);
    return Math.sign(offset) * arc / this.arcStops[1] * this.step;
  }

  /**
   * Offset of an item from the current position, wrapped to the nearest side of the loop.
   */
  getOffset(index) {
    const count = this.items.length;
    return gsap.utils.wrap(-count / 2, count / 2, index - this.state.position);
  }

  getActiveSlide() {
    const count = this.items.length;
    const index = gsap.utils.wrap(0, count, Math.round(this.state.position));
    return index % this.total;
  }

  render() {
    this.items.forEach((item, index) => {
      const offset = this.getOffset(index);
      const distance = Math.abs(offset);
      const opacity = gsap.utils.clamp(0, 1, this.visibleSide + 1 - distance);
      this.setRotation[index](this.getAngle(offset));
      this.setScale[index](this.getScale(offset));
      item.style.zIndex = Math.round(100 - distance * 10);
      item.style.opacity = opacity;
      item.style.visibility = opacity > 0 ? '' : 'hidden';
      item.toggleAttribute('data-radial-active', distance < 0.5);
    });

    const activeSlide = this.getActiveSlide();
    if(activeSlide !== this.activeSlide) {
      this.activeSlide = activeSlide;
      this.dots.forEach((dot, index) => {
        const isActive = index === activeSlide;
        dot.setAttribute('data-state', isActive ? 'active' : 'inactive');
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
      this.dispatchEvent(new CustomEvent('slide', { detail: { currentSlide: activeSlide } }));
    }
  }

  tweenTo(position) {
    gsap.killTweensOf(this.state);
    gsap.to(this.state, {
      position,
      duration: this.duration,
      ease: 'power3.out',
      onUpdate: this.render,
      onComplete: () => this.syncProxy()
    });
  }

  /**
   * Moves to the nearest copy of an original slide.
   */
  goToSlide(slide) {
    let bestOffset = null;
    this.items.forEach((item, index) => {
      if(index % this.total !== slide) return;
      const offset = this.getOffset(index);
      if(bestOffset === null || Math.abs(offset) < Math.abs(bestOffset)) {
        bestOffset = offset;
      }
    });
    if(bestOffset === null) return;
    this.tweenTo(Math.round(this.state.position + bestOffset));
  }

  next() {
    this.tweenTo(Math.round(this.state.position) + 1);
  }

  prev() {
    this.tweenTo(Math.round(this.state.position) - 1);
  }

  // Matches the carousel-block API so product-form.js can jump to variant media
  jumpTo(index) {
    this.goToSlide(parseInt(index));
  }

  setupZoom() {
    this.zoom = this.querySelector('[data-radial-zoom]');
    if(!this.zoom) return;
    this.zoomStage = this.zoom.querySelector('[data-radial-zoom-stage]');
    // The image classes live in the markup so Tailwind generates them
    const classSource = this.zoom.querySelector('[data-radial-zoom-image-classes]');
    this.zoomImage = document.createElement('img');
    this.zoomImage.className = classSource.className;
    this.zoomImage.classList.remove('hidden');
    this.zoomImage.alt = '';
    classSource.replaceWith(this.zoomImage);
    this.zoomImage.addEventListener('load', () => this.zoom.setAttribute('data-state', 'loaded'));
    this.zoomImage.addEventListener('error', () => this.zoom.setAttribute('data-state', 'loaded'));

    this.zoom.querySelector('[data-radial-zoom-close]').addEventListener('click', () => this.closeZoom());
    this.zoom.addEventListener('click', event => {
      // Close when clicking anywhere except the image itself
      if(event.target !== this.zoomImage) this.closeZoom();
    });
    this.zoom.addEventListener('cancel', event => {
      event.preventDefault();
      this.closeZoom();
    });
  }

  openZoom(card) {
    if(!this.zoom || this.zoom.open) return;
    const preview = card.querySelector('img');
    this.zoom.setAttribute('data-state', 'loading');
    this.zoomImage.removeAttribute('src');
    if(preview) {
      this.zoomImage.width = preview.naturalWidth || preview.width;
      this.zoomImage.height = preview.naturalHeight || preview.height;
    }
    this.zoomImage.alt = card.dataset.zoomAlt || preview?.alt || '';
    this.zoomImage.src = card.dataset.zoomSrc;

    document.documentElement.style.overflow = 'hidden';
    this.zoom.showModal();
    gsap.fromTo(this.zoom, { opacity: 0 }, { opacity: 1, duration: this.reducedMotion ? 0 : 0.3, ease: 'power2.out' });
    gsap.fromTo(this.zoomStage, { scale: 0.92 }, { scale: 1, duration: this.reducedMotion ? 0 : 0.4, ease: 'power3.out' });
  }

  closeZoom() {
    if(!this.zoom?.open) return;
    gsap.to(this.zoom, {
      opacity: 0,
      duration: this.reducedMotion ? 0 : 0.25,
      ease: 'power2.in',
      onComplete: () => {
        this.zoom.close();
        document.documentElement.style.overflow = '';
      }
    });
  }

  onClick(event) {
    if(event.target.closest('[data-radial-zoom]')) return;
    if(event.target.closest('[data-radial-dot]')) return;
    // Ignore the click the browser fires at the end of a drag
    if(performance.now() - this.lastDragEnd < 150) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    // Clicking a side card brings it to the centre instead of playing its video
    const item = event.target.closest('[data-radial-item]');
    if(!item) return;
    if(!item.hasAttribute('data-radial-active')) {
      event.preventDefault();
      event.stopPropagation();
      this.tweenTo(Math.round(this.state.position + this.getOffset(this.items.indexOf(item))));
      return;
    }
    // Clicking the active image card zooms it
    const card = item.querySelector('[data-radial-card][data-zoom-src]');
    if(card) {
      event.preventDefault();
      event.stopPropagation();
      this.openZoom(card);
    }
  }

  onKeydown(event) {
    if(this.zoom?.open) return;
    if(event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    }
    else if(event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
    }
  }
}

customElements.define('radial-slider', RadialSlider);
